import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { FunctionsHttpError } from '@supabase/supabase-js';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  actionsTaken?: string[];
}

interface UseChatReturn {
  messages: ChatMessage[];
  sendMessage: (content: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  clearChat: () => void;
}

export interface PageContext {
  current_module: string;
  current_entity_id?: string;
  current_entity_type?: string;
}

function extractPageContext(): PageContext {
  const path = window.location.pathname;
  const segments = path.split('/').filter(Boolean);

  if (segments.length === 0) return { current_module: 'home' };

  const [first, second] = segments;

  if (first === 'work-orders') {
    if (second) return { current_module: 'maintenance', current_entity_type: 'work_order', current_entity_id: second };
    return { current_module: 'maintenance' };
  }
  if (first === 'molds') {
    if (second) return { current_module: 'molds', current_entity_type: 'mold', current_entity_id: second };
    return { current_module: 'molds' };
  }
  if (first === 'spare-parts') {
    if (second) return { current_module: 'spare-parts', current_entity_type: 'spare_part', current_entity_id: second };
    return { current_module: 'spare-parts' };
  }
  if (first === 'readiness') return { current_module: 'readiness' };
  if (first === 'reports') return { current_module: 'reports' };
  if (first === 'settings') return { current_module: 'settings' };

  return { current_module: 'home' };
}

function generateId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function useChat(): UseChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: generateId(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setError(null);

    try {
      // Build history including the just-added user message
      const history = messages.map(m => ({ role: m.role, content: m.content }));
      history.push({ role: 'user', content: userMessage.content });

      const { data, error: invokeError } = await supabase.functions.invoke('chat', {
        body: {
          messages: history,
          context: extractPageContext(),
        },
      });

      if (invokeError) {
        const msg = invokeError instanceof FunctionsHttpError
          ? ((await invokeError.context.json().catch(() => ({}))) as { error?: string }).error ?? invokeError.message
          : invokeError.message;
        throw new Error(msg);
      }

      const assistantMessage: ChatMessage = {
        id: generateId(),
        role: 'assistant',
        content: data.reply,
        timestamp: new Date(),
        actionsTaken: data.actions_taken?.length ? data.actions_taken : undefined,
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setError(msg);

      // Add error as assistant message so it appears in the chat
      setMessages(prev => [
        ...prev,
        {
          id: generateId(),
          role: 'assistant',
          content: `Sorry, I ran into an issue: ${msg}`,
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [messages, isLoading]);

  const clearChat = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  return { messages, sendMessage, isLoading, error, clearChat };
}
